import { formatZodError, type FormatZodErrorOptions } from "@Simplicity-Education/core";

const apiBase = import.meta.env.VITE_API_URL ?? "";
const RAW_TEXT_LIMIT = 500;

/**
 * A non-OK response: `message` is the server's readable wording plus its code, status and any
 * `data.detail` (web-admin is staff-only, so it shows more, not less); `body` is the parsed JSON.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: Record<string, unknown> | null;

  constructor(message: string, status: number, body: Record<string, unknown> | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function parseJson(text: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(text);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** True for an oRPC validation body, whose issues sit under `data.issues`. */
export function hasIssues(body: Record<string, unknown>): boolean {
  const data = body.data;
  return typeof data === "object" && data !== null && "issues" in data;
}

function nonEmptyString(record: unknown, key: string): string | undefined {
  if (typeof record !== "object" || record === null) return undefined;
  const value = (record as Record<string, unknown>)[key];
  return typeof value === "string" && value !== "" ? value : undefined;
}

function readablePart(body: Record<string, unknown>, options?: FormatZodErrorOptions): string {
  if (hasIssues(body)) return formatZodError(body, options);
  return nonEmptyString(body, "message") ?? "Request failed";
}

function detailsSuffix(body: Record<string, unknown>, status: number): string {
  const code = nonEmptyString(body, "code");
  const detail = nonEmptyString(body.data, "detail");
  const head = code ? `${code} (${status})` : `(${status})`;
  return ` — ${head}${detail ? `: ${detail}` : ""}`;
}

function truncate(text: string): string {
  return text.length > RAW_TEXT_LIMIT ? `${text.slice(0, RAW_TEXT_LIMIT)}…` : text;
}

function errorMessageFrom(
  body: Record<string, unknown> | null,
  text: string,
  status: number,
  options?: FormatZodErrorOptions,
): string {
  if (body) return readablePart(body, options) + detailsSuffix(body, status);
  if (text) return `Request failed — ${status}: ${truncate(text)}`;
  // Keep this exact shape: sentry.ts drops /HTTP 40[13]/ as a lost session, not a real error.
  return `HTTP ${status}`;
}

/** Reads a failed response into an ApiError; validation issues become readable lines. */
export async function apiErrorFrom(
  response: Response,
  options?: FormatZodErrorOptions,
): Promise<ApiError> {
  const text = await response.text();
  const body = parseJson(text);
  return new ApiError(
    errorMessageFrom(body, text, response.status, options),
    response.status,
    body,
  );
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (response.status === 401 || response.status === 403) {
    const base = window.location.pathname.startsWith("/web-admin") ? "/web-admin" : "";
    window.location.href = `${base}/login`;
    throw new Error("Unauthorized");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    throw await apiErrorFrom(response);
  }

  return response.json();
}
